import React, { useRef, useState } from 'react';
import {
    Box,
    Typography,
    Container,
    Paper,
    Button,
    Alert,
    CircularProgress,
    Stack,
} from '@mui/material';
import NavBar from '../../Components/NavBar/navbar';
import FooterSection from '../../Components/Footer/footer';
import GraphicEqIcon from '@mui/icons-material/GraphicEq';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { THEME, pageBackgroundSx, gridOverlaySx } from '../../theme';
import { API_ENDPOINTS, ART_SERVICE_BASE_URL } from '../../utils/config';

export default function AudioVisualizerPage() {
    const canvasRef = useRef(null);
    const analyserRef = useRef(null);
    const audioCtxRef = useRef(null);
    const rafRef = useRef(null);
    const [active, setActive] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [loading, setLoading] = useState(false);
    const [imageFile, setImageFile] = useState(null);
    const [audioFile, setAudioFile] = useState(null);
    const [videoUrl, setVideoUrl] = useState('');
    const [mode, setMode] = useState('');

    const startMic = async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
            const source = audioCtx.createMediaStreamSource(stream);
            const analyser = audioCtx.createAnalyser();
            analyser.fftSize = 256;
            source.connect(analyser);
            audioCtxRef.current = audioCtx;
            analyserRef.current = analyser;
            setActive(true);
            setError('');
            draw();
        } catch (err) {
            setError("Can't use the mic — check permissions.");
        }
    };

    const stopMic = () => {
        if (rafRef.current) cancelAnimationFrame(rafRef.current);
        if (audioCtxRef.current) audioCtxRef.current.close();
        analyserRef.current = null;
        audioCtxRef.current = null;
        setActive(false);
    };

    const draw = () => {
        const canvas = canvasRef.current;
        const analyser = analyserRef.current;
        if (!canvas || !analyser) return;

        const ctx = canvas.getContext('2d');
        const buffer = new Uint8Array(analyser.frequencyBinCount);

        const render = () => {
            analyser.getByteFrequencyData(buffer);
            const w = canvas.width;
            const h = canvas.height;
            ctx.fillStyle = '#000';
            ctx.fillRect(0, 0, w, h);

            const barWidth = w / buffer.length;
            buffer.forEach((val, i) => {
                const barHeight = (val / 255) * h;
                const g = Math.floor((val / 255) * 200);
                ctx.fillStyle = `rgb(44,${g + 87},93)`;
                ctx.fillRect(i * barWidth, h - barHeight, barWidth - 1, barHeight);
            });
            rafRef.current = requestAnimationFrame(render);
        };
        render();
    };

    const resolveVideoUrl = (path) => {
        if (!path) return '';
        if (path.startsWith('http')) return path;
        return `${ART_SERVICE_BASE_URL}${path.startsWith('/') ? '' : '/'}${path}`;
    };

    const runMusicViz = async () => {
        setError('');
        setSuccess('');
        setVideoUrl('');
        if (!imageFile || !audioFile) {
            setError('Upload both an image and an audio file.');
            return;
        }
        setLoading(true);
        try {
            const body = new FormData();
            body.append('image', imageFile);
            body.append('audio', audioFile);
            const resp = await fetch(API_ENDPOINTS.AUDIO_VISUALIZER, {
                method: 'POST',
                body,
            });
            const data = await resp.json();
            if (!resp.ok || data.success === false) {
                // Keep API detail in the console; show a short message in the UI.
                console.error('Visualization failed:', data.error || data);
                setError("Couldn't create the visualization. Try again.");
                return;
            }
            const url = resolveVideoUrl(data.video_url || data.download_url);
            setVideoUrl(url);
            setMode(data.mode || '');
            setSuccess('Video ready.');
        } catch (err) {
            console.error('Audio visualizer request failed:', err);
            setError("Server not responding. Try again later.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box sx={pageBackgroundSx}>
            <Box sx={gridOverlaySx} />
            <NavBar />
            <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1, pt: 12, pb: 8 }}>
                <Typography variant="overline" sx={{ color: THEME.primary, fontWeight: 'bold', letterSpacing: 2 }}>
                    EVOLVING SYSTEMS / ART
                </Typography>
                <Typography variant="h3" fontWeight={800} color={THEME.textMain} sx={{ mb: 1 }}>
                    Audio-Visualizer
                </Typography>
                <Typography variant="h6" color={THEME.secondary} sx={{ mb: 3 }}>
                    Upload an image and audio to generate a Turing-driven video — or watch live mic bars.
                </Typography>

                {(error || success) && (
                    <Box sx={{ mb: 2 }}>
                        {error && <Alert severity="error">{error}</Alert>}
                        {success && <Alert severity="success">{success}</Alert>}
                    </Box>
                )}

                <Paper sx={{ bgcolor: THEME.cardBg, border: `1px solid ${THEME.border}`, borderRadius: '12px', p: 3, mb: 3 }}>
                    <Typography variant="subtitle1" color={THEME.primary} fontWeight={700} mb={2}>
                        Music visualization
                    </Typography>
                    <Stack spacing={2} direction={{ xs: 'column', md: 'row' }} alignItems="center" mb={2}>
                        <Button
                            variant="outlined"
                            component="label"
                            startIcon={<UploadFileIcon />}
                            sx={{ borderColor: THEME.border, color: THEME.textMain, textTransform: 'none' }}
                        >
                            {imageFile ? imageFile.name : 'Choose image'}
                            <input
                                type="file"
                                hidden
                                accept="image/*"
                                onChange={(e) => setImageFile(e.target.files?.[0] || null)}
                            />
                        </Button>
                        <Button
                            variant="outlined"
                            component="label"
                            startIcon={<UploadFileIcon />}
                            sx={{ borderColor: THEME.border, color: THEME.textMain, textTransform: 'none' }}
                        >
                            {audioFile ? audioFile.name : 'Choose audio'}
                            <input
                                type="file"
                                hidden
                                accept="audio/*,.mp3,.wav,.ogg,.flac,.m4a"
                                onChange={(e) => setAudioFile(e.target.files?.[0] || null)}
                            />
                        </Button>
                        <Button
                            variant="contained"
                            onClick={runMusicViz}
                            disabled={loading}
                            startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <GraphicEqIcon />}
                            sx={{
                                bgcolor: THEME.primary,
                                color: '#000',
                                fontWeight: 700,
                                textTransform: 'none',
                                '&:hover': { bgcolor: THEME.primaryHover },
                            }}
                        >
                            {loading ? 'Generating…' : 'Generate video'}
                        </Button>
                    </Stack>
                    {mode && (
                        <Typography variant="caption" color={THEME.secondary}>
                            Mode: {mode}
                        </Typography>
                    )}
                    {videoUrl && (
                        <Box
                            sx={{
                                mt: 2,
                                width: '100%',
                                height: 320,
                                borderRadius: '8px',
                                overflow: 'hidden',
                                border: `1px solid ${THEME.border}`,
                                bgcolor: '#000',
                            }}
                        >
                            <video
                                key={videoUrl}
                                src={videoUrl}
                                controls
                                playsInline
                                style={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'cover',
                                    display: 'block',
                                }}
                            />
                        </Box>
                    )}
                </Paper>

                <Paper sx={{ bgcolor: THEME.cardBg, border: `1px solid ${THEME.border}`, borderRadius: '12px', p: 3 }}>
                    <Typography variant="subtitle1" color={THEME.primary} fontWeight={700} mb={2}>
                        Live mic
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                        <Button
                            variant="contained"
                            startIcon={<GraphicEqIcon />}
                            onClick={active ? stopMic : startMic}
                            sx={{ bgcolor: THEME.primary, color: '#000', '&:hover': { bgcolor: THEME.primaryHover } }}
                        >
                            {active ? 'Stop' : 'Start Microphone'}
                        </Button>
                    </Box>
                    <canvas
                        ref={canvasRef}
                        width={800}
                        height={320}
                        style={{
                            width: '100%',
                            maxWidth: 800,
                            border: `1px solid ${THEME.border}`,
                            borderRadius: 8,
                            background: '#000',
                        }}
                    />
                </Paper>
            </Container>
            <FooterSection />
        </Box>
    );
}
